import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { api } from '../lib/api';
import { MessageSquare, Send, X, Bot } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function FloatingAssistant({ open, onToggle }) {
  const { token } = useAuth();
  const { sessionId, messages, sending, addMessage, setSending } = useChat();
  const [input, setInput] = useState('');
  const listRef = useRef(null);

  const toggle = () => onToggle(v => !v);

  const scrollBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { scrollBottom(); }, [messages, scrollBottom]);

  const sendMessage = async () => {
    if (!token || !input.trim() || sending) return;
    const text = input.trim();
    addMessage({ id: 'u-' + Date.now(), role: 'user', content: text });
    setInput('');
    setSending(true);
    try {
      const res = await api.aiChat(token, { message: text, session_id: sessionId, source: 'text' });
      addMessage({
        id: 'a-' + Date.now(),
        role: 'assistant',
        content: res.reply,
        action_request_id: res.action_request_id ?? null,
        action_preview: res.action_preview ?? null,
        action_status: res.action_request_id ? 'pending' : undefined,
      });
    } catch (e) {
      addMessage({ id: 'e-' + Date.now(), role: 'assistant', content: `Error: ${e?.message ?? 'AI failed'}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={toggle}
        data-testid="floating-assistant-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink shadow-accent flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        aria-label="Toggle AI Assistant"
      >
        {open ? <X size={22} className="text-white" /> : <MessageSquare size={22} className="text-white" />}
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-[380px] h-[520px] rounded-3xl overflow-hidden border border-white/10 bg-navy-900/95 backdrop-blur-xl shadow-2xl flex flex-col"
            data-testid="floating-assistant-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/8 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-purple to-brand-teal flex items-center justify-center">
                <Bot size={18} className="text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Concierge</p>
                <p className="text-white/40 text-xs">Ask me anything</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center pt-12">
                  <Bot size={40} className="text-brand-purple/40 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">Ask me anything about your business</p>
                </div>
              )}
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    data-testid={`chat-msg-${msg.id}`}
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-br from-brand-purple to-brand-purple-deep text-white rounded-br-md'
                        : 'bg-white/8 text-white/90 rounded-bl-md'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-white/40 text-sm">
                  <div className="w-2 h-2 rounded-full bg-brand-purple animate-pulse" />
                  Thinking...
                </div>
              )}
            </div>

            {/* Input */}
            <div className="px-4 pb-4 pt-2">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-4 py-2">
                <input
                  data-testid="floating-chat-input"
                  className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none"
                  placeholder="Type a message..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  data-testid="floating-chat-send"
                  className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-purple to-brand-purple-deep flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                >
                  <Send size={14} className="text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
